/**
 * ProjectCommsSheet — the mobile (≤860px) home for the project Conversation.
 *
 * On phones the persistent `.project-comms-col` in ProjectDetail is
 * display:none (see the mobile block in components.css). This full-height,
 * slide-up sheet is opened by the green chat FAB (or the "✦ Ask" header entry
 * via openAsk) and MOUNTS THE REAL <ProjectThreadPanel> unchanged — same data
 * source, same message rendering (AI-drafted messages stay violet), same
 * compose bar. It is a re-mount of the real component, never a redraw.
 *
 * Accessibility (WCAG 2.2 AA):
 *   - role="dialog" + aria-modal="true" + aria-label (the conversation subject).
 *   - Focus moves to the close button on open; focus is trapped inside the
 *     sheet (Tab / Shift+Tab cycle); focus is restored to the opener on close.
 *   - Esc closes.
 *
 * No-nested-scroll rule: the sheet body is overflow:hidden — the ONLY scroll
 * inside the sheet is ProjectThreadPanel's own message list. The page body
 * scroll behind is locked while the sheet is open.
 */
import { useEffect, useRef } from 'react';
import ProjectThreadPanel from './ProjectThreadPanel';
import ProjectErrorBoundary from './ProjectErrorBoundary';

interface ProjectCommsSheetProps {
  projectId: string;
  /** Optional — the linked client, used by ProjectThreadPanel for outbound sends. */
  clientId?: string;
  /** Display name for the sheet title ("Conversation · {clientName}"). */
  clientName?: string;
  onClose: () => void;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function ProjectCommsSheet({
  projectId,
  clientId,
  clientName,
  onClose,
}: ProjectCommsSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const hasName = Boolean(clientName && clientName !== '—');
  const title = hasName ? `Conversation · ${clientName}` : 'Conversation';
  const dialogLabel = hasName
    ? `Conversation with ${clientName}`
    : 'Project conversation';

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    // Focus the close button on open (a safe, always-present focus target).
    closeRef.current?.focus();

    // Lock the background scroll container(s) so nothing scrolls behind the
    // full-screen sheet. On mobile the real scroller is `.pbody`; body is
    // locked too as a belt-and-suspenders.
    const pbody = document.querySelector('.pbody') as HTMLElement | null;
    const prevBody = document.body.style.overflow;
    const prevPbody = pbody?.style.overflow ?? '';
    document.body.style.overflow = 'hidden';
    if (pbody) pbody.style.overflow = 'hidden';

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const sheet = sheetRef.current;
      if (!sheet) return;
      const nodes = Array.from(
        sheet.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (nodes.length === 0) {
        e.preventDefault();
        closeRef.current?.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !sheet.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevBody;
      if (pbody) pbody.style.overflow = prevPbody;
      opener?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      ref={sheetRef}
      className="pcomms-sheet"
      role="dialog"
      aria-modal="true"
      aria-label={dialogLabel}
    >
      <div className="pcomms-sheet-head">
        <span className="pcomms-sheet-title">{title}</span>
        <button
          ref={closeRef}
          type="button"
          className="pcomms-sheet-x"
          onClick={onClose}
          aria-label="Close conversation"
          title="Close (Esc)"
        >
          ✕
        </button>
      </div>
      <div className="pcomms-sheet-body">
        <ProjectErrorBoundary surface="Project conversation">
          <ProjectThreadPanel
            projectId={projectId}
            clientId={clientId}
            clientName={clientName}
          />
        </ProjectErrorBoundary>
      </div>
    </div>
  );
}
